import * as React from 'react';
import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { Button, Grid, Row, Spacer } from '@nextui-org/react';
import produce from 'immer';
import { DateTime } from 'luxon';
import { useRouter } from 'next/router';
import { ROUTES } from '@src/config/constants';
import { AppFullWidthContainer } from '@src/layouts/app/AppFullWidthContainer';
import { AppTitle } from '@src/layouts/app/AppTitle';
import { AddNew } from '@src/layouts/feed/AddNew';
import { decodeQueryParams, encodeQueryParams } from '@src/util/navigation';

interface AssetFiltersProps {}

/**
 * @name AssetFilters
 * @description tbd...
 */
export function AssetFilters(_props: AssetFiltersProps) {
  // const {} = props

  // Hooks
  const router = useRouter();

  // Setup
  const { query } = router;
  const { clients, endDate, handles, startDate, statuses, tags } =
    decodeQueryParams(query);

  // Handlers
  const onClientPress = (client: string) => {
    const currentQuery = decodeQueryParams(query);
    const newQuery = produce(currentQuery, (draft) => {
      if (draft.clients) {
        draft.clients = draft.clients.filter((c) => c !== client);
      }
    });

    router.push({
      pathname: ROUTES.feed,
      query: encodeQueryParams(newQuery),
    });
  };

  const onTagPress = (tag: string) => {
    const currentQuery = decodeQueryParams(query);
    const newQuery = produce(currentQuery, (draft) => {
      if (draft.tags) {
        draft.tags = draft.tags.filter((t) => t !== tag);
      }
    });

    router.push({
      pathname: ROUTES.feed,
      query: encodeQueryParams(newQuery),
    });
  };

  const onHandlePress = (handle: string) => {
    const currentQuery = decodeQueryParams(query);
    const newQuery = produce(currentQuery, (draft) => {
      if (draft.handles) {
        draft.handles = draft.handles.filter((h) => h !== handle);
      }
    });

    router.push({
      pathname: ROUTES.feed,
      query: encodeQueryParams(newQuery),
    });
  };

  const onStatusPress = (status: string) => {
    const currentQuery = decodeQueryParams(query);
    const newQuery = produce(currentQuery, (draft) => {
      if (draft.statuses) {
        draft.statuses = draft.statuses.filter((h) => h !== status);
      }
    });

    router.push({
      pathname: ROUTES.feed,
      query: encodeQueryParams(newQuery),
    });
  };

  const onStartDatePress = () => {
    const currentQuery = decodeQueryParams(query);
    const newQuery = produce(currentQuery, (draft) => {
      if (draft.startDate) {
        delete draft.startDate;
      }
    });

    router.push({
      pathname: ROUTES.feed,
      query: encodeQueryParams(newQuery),
    });
  };

  const onEndDatePress = () => {
    const currentQuery = decodeQueryParams(query);
    const newQuery = produce(currentQuery, (draft) => {
      if (draft.endDate) {
        delete draft.endDate;
      }
    });

    router.push({
      pathname: ROUTES.feed,
      query: encodeQueryParams(newQuery),
    });
  };

  return (
    <AppFullWidthContainer>
      <Spacer />
      <Row align="center">
        <AppTitle color="gray">
          <AdjustmentsHorizontalIcon width={30} height={30} />
          Filters
        </AppTitle>
        <Spacer x={1} />
        <AddNew />
      </Row>
      <Spacer y={0.3} />
      <Row>
        <Grid.Container gap={0.5}>
          {clients?.map((client, index) => {
            return (
              <Grid key={`client#${index}`}>
                <Button
                  size="sm"
                  auto
                  flat
                  color="error"
                  css={{ zIndex: 0 }}
                  iconRight={<XMarkIcon width={20} height={20} />}
                  onPress={() => onClientPress(client)}
                >
                  {client}
                </Button>
              </Grid>
            );
          })}

          {tags?.map((tag, index) => {
            return (
              <Grid key={`tag#${index}`}>
                <Button
                  size="sm"
                  auto
                  flat
                  color="primary"
                  css={{ zIndex: 0 }}
                  iconRight={<XMarkIcon width={20} height={20} />}
                  onPress={() => onTagPress(tag)}
                >
                  {tag}
                </Button>
              </Grid>
            );
          })}

          {handles?.map((handle, index) => {
            return (
              <Grid key={`handle#${index}`}>
                <Button
                  size="sm"
                  auto
                  flat
                  color="secondary"
                  css={{ zIndex: 0 }}
                  iconRight={<XMarkIcon width={20} height={20} />}
                  onPress={() => onHandlePress(handle)}
                >
                  {handle}
                </Button>
              </Grid>
            );
          })}

          {statuses?.map((status, index) => {
            return (
              <Grid key={`handle#${index}`}>
                <Button
                  size="sm"
                  auto
                  flat
                  color="success"
                  css={{ zIndex: 0 }}
                  iconRight={<XMarkIcon width={20} height={20} />}
                  onPress={() => onStatusPress(status)}
                >
                  {status}
                </Button>
              </Grid>
            );
          })}

          {startDate && (
            <Grid>
              <Button
                size="sm"
                auto
                flat
                color="warning"
                css={{ zIndex: 0 }}
                iconRight={<XMarkIcon width={20} height={20} />}
                onPress={onStartDatePress}
              >
                {DateTime.fromJSDate(startDate).toFormat('MM/dd/yyyy')}
              </Button>
            </Grid>
          )}

          {endDate && (
            <Grid>
              <Button
                size="sm"
                auto
                flat
                color="warning"
                css={{ zIndex: 0 }}
                iconRight={<XMarkIcon width={20} height={20} />}
                onPress={onEndDatePress}
              >
                {DateTime.fromJSDate(endDate).toFormat('MM/dd/yyyy')}
              </Button>
            </Grid>
          )}
        </Grid.Container>
      </Row>
    </AppFullWidthContainer>
  );
}
